import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import {
  Button,
  SIZE,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import { StyledWhiteContainer } from "features/metadesk/settings/common.styles";
import useDiscardConfirm from "../hook/useDiscardConfirm";

function SaveDiscardActions({
  onDiscard,
  onSave,
  isDisabled,
  hasSaved: externalHasSaved,
  setHasSaved: externalSetHasSaved,
  showDiscardConfirm = true,
}) {
  const { formatMessage } = useIntl();
  const { handleDiscardConfirm, resetIsConfirmed, isConfirmed } =
    useDiscardConfirm();

  const handleDiscard = () => {
    if (showDiscardConfirm) {
      handleDiscardConfirm(() => {
        onDiscard();
        resetIsConfirmed();
      });
    } else {
      onDiscard();
    }
  };

  // Determine whether to use the external hasSaved or manage it ourselves
  // when there is a situation where this component will be removed and rerendered, we need to use the external hasSaved to keep the state
  const [internalHasSaved, setInternalHasSaved] = useState(false);
  const hasSaved = externalHasSaved ?? internalHasSaved;
  const setHasSaved = externalSetHasSaved ?? setInternalHasSaved;

  // If it's not disabled, it means there's a change, so it's "Save"
  useEffect(() => {
    if (!isDisabled) {
      setHasSaved(false);
    }
  }, [isDisabled]);

  const handleSave = async () => {
    await onSave();
    setHasSaved(true);
  };

  const saveButtonText =
    hasSaved && isDisabled
      ? formatMessage({ id: "global.btn.saved" })
      : formatMessage({ id: "global.btn.save" });

  return (
    <StyledWhiteContainer
      style={{ marginBottom: "12px", padding: "16px 32px 24px" }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size={SIZE.XL}
          variant={VARIANT.OUTLINED}
          disabled={isDisabled}
          sx={{ width: 120 }}
          onClick={handleDiscard}
        >
          {formatMessage({
            id: "global.btn.discard",
          })}
        </Button>
        <Button
          disabled={isDisabled}
          size={SIZE.XL}
          sx={{ ml: "12px", width: 120 }}
          onClick={handleSave}
        >
          {saveButtonText}
        </Button>
      </Box>
    </StyledWhiteContainer>
  );
}

SaveDiscardActions.propTypes = {
  onDiscard: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

SaveDiscardActions.defaultProps = {
  isDisabled: false,
};

export default SaveDiscardActions;
